
import React, { useState, useEffect } from 'react';
import { LayoutDashboard, FileText, Calculator, CalendarDays, FlaskConical, LogOut, BookOpen, Activity, Table, ChevronRight, Settings, BedDouble, Loader2, Lock } from 'lucide-react';
import { Dashboard } from './components/Dashboard';
import { ReportGenerator } from './components/ReportGenerator';
import { DrugCalculator } from './components/DrugCalculator';
import { CalendarSystem } from './components/CalendarSystem';
import { LabManager } from './components/LabManager';
import { Login } from './components/Login';
import { Education } from './components/Education';
import { CaseLogs } from './components/CaseLogs';
import { AccountSettings } from './components/AccountSettings';
import { InpatientManager } from './components/InpatientManager';
import { User, CalendarEvent, RotationWeek } from './types';
import { dbService, authService } from './lib/firebase';

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  
  useEffect(() => {
      document.documentElement.classList.add('dark');
  }, []);

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [rotations, setRotations] = useState<RotationWeek[]>([]);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (firebaseUser) => {
        if (firebaseUser) {
            const profile = await dbService.getUserProfile(firebaseUser.uid);
            if (profile) {
                setUser(profile);
            } else {
                if (firebaseUser.email === 'kevinsgrisham@gmail.com') {
                     setUser({
                        id: firebaseUser.uid,
                        email: firebaseUser.email || '',
                        firstName: 'Kevin',
                        lastName: 'Grisham',
                        role: 'Resident',
                        isAdmin: true,
                        allowedViews: ['dashboard', 'inpatients', 'reports', 'caselogs', 'calendar', 'labs', 'education', 'drugs', 'settings']
                    });
                } else {
                    setUser(null);
                }
            }
        } else {
            setUser(null);
        }
        setIsAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
        const unsubEvents = dbService.subscribeToEvents((data) => setEvents(data));
        let unsubRotations = () => {};
        unsubRotations = dbService.subscribeToRotations((data) => setRotations(data));
        
        return () => {
            unsubEvents();
            unsubRotations();
        };
    }
  }, [user]);

  const isViewAllowed = (view: string) => {
      if (!user) return false;
      if (user.isAdmin || user.id === '8' || user.email === 'kevinsgrisham@gmail.com') return true; 
      if (view === 'settings') return true; 
      
      const allowedViews = user.allowedViews || ['dashboard', 'inpatients', 'reports', 'caselogs', 'calendar', 'labs', 'education', 'drugs', 'settings'];
      return allowedViews.includes(view);
  };

  useEffect(() => {
      if (user && !isAuthLoading && !isViewAllowed(currentView)) {
          const allPotentialViews = ['dashboard', 'inpatients', 'reports', 'caselogs', 'calendar', 'labs', 'education', 'drugs', 'settings'];
          const firstAllowed = allPotentialViews.find(v => isViewAllowed(v));
          if (firstAllowed) {
              setCurrentView(firstAllowed);
          } else {
              setCurrentView('settings');
          }
      }
  }, [user, isAuthLoading, currentView]);

  if (isAuthLoading) {
      return (
          <div className="h-screen w-screen bg-slate-950 flex flex-col items-center justify-center text-white gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-indigo-500" />
              <p className="text-slate-400 text-sm font-medium animate-pulse">Authenticating...</p>
          </div>
      );
  }

  if (!user) {
    return <Login onLogin={setUser} />;
  }

  const handleLogout = () => {
    authService.logout();
    setUser(null);
    setCurrentView('dashboard');
  };

  const handleSaveEvent = async (event: CalendarEvent) => {
    await dbService.saveEvent(event);
  };

  const handleDeleteEvent = async (date: string, category: string) => {
      await dbService.deleteEvent(date, category);
  }

  const handleSaveRotation = async (rotation: RotationWeek) => {
      await dbService.saveRotation(rotation);
  }

  const renderContent = () => {
    if (!isViewAllowed(currentView)) {
        return (
            <div className="h-full flex flex-col items-center justify-center text-slate-500 space-y-4">
                <div className="p-6 bg-slate-900 rounded-full border border-slate-800">
                    <Lock className="w-12 h-12 text-slate-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-400">Access Restricted</h2>
                <p className="text-sm">You do not have permission to view this module.</p>
                <button onClick={() => setCurrentView('settings')} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-500 transition-colors">
                    Go to Account Settings
                </button>
            </div>
        );
    }

    switch (currentView) {
      case 'dashboard':
        return <Dashboard navigate={setCurrentView} currentUser={user} events={events} />;
      case 'reports':
        return <ReportGenerator />;
      case 'drugs':
        return <DrugCalculator />;
      case 'calendar':
        return <CalendarSystem 
                  events={events} 
                  rotations={rotations}
                  onSaveEvent={handleSaveEvent} 
                  onDeleteEvent={handleDeleteEvent}
                  onSaveRotation={handleSaveRotation}
                />;
      case 'labs':
        return <LabManager />;
      case 'education':
        return <Education />;
      case 'caselogs':
        return <CaseLogs />;
      case 'inpatients':
        return <InpatientManager />;
      case 'settings':
        return <AccountSettings currentUser={user} onUpdateUser={setUser} />;
      default:
        return <Dashboard navigate={setCurrentView} currentUser={user} events={events} />;
    }
  };

  const NavItem = ({ view, icon: Icon, label }: { view: string; icon: any; label: string }) => {
    if (!isViewAllowed(view)) return null;

    const isActive = currentView === view;
    return (
      <button
        onClick={() => setCurrentView(view)}
        className={`relative w-full flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 group font-sans overflow-hidden ${
          isActive
            ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-lg shadow-indigo-900/30'
            : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
        }`}
      >
        {isActive && <div className="absolute inset-0 bg-white/10 blur-md opacity-30"></div>}
        
        <Icon className={`w-5 h-5 relative z-10 transition-transform duration-300 ${isActive ? 'scale-110' : 'group-hover:scale-110'}`} />
        <span className="font-semibold tracking-wide text-sm relative z-10">{label}</span>
        
        {isActive && <ChevronRight className="w-4 h-4 absolute right-4 opacity-70 animate-in slide-in-from-left-2" />}
      </button>
    );
  };

  return (
    <div className="flex h-screen bg-slate-950 text-slate-100 font-sans overflow-hidden">
      {/* Sidebar */}
      <aside className="w-80 bg-slate-900 hidden md:flex flex-col p-6 relative shadow-2xl z-20 backdrop-blur-xl border-r border-slate-800">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
            <div className="absolute top-[-20%] left-[-20%] w-[80%] h-[40%] bg-indigo-900/10 rounded-full blur-[80px]"></div>
            <div className="absolute bottom-[-10%] right-[-20%] w-[80%] h-[40%] bg-blue-900/5 rounded-full blur-[80px]"></div>
        </div>

        <div className="mb-6 flex flex-col items-center text-center relative group cursor-default pt-2">
            <div className="relative w-12 h-12 mb-3 bg-gradient-to-br from-indigo-500 via-blue-600 to-teal-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-900/30 group-hover:shadow-indigo-500/40 transition-all duration-500">
                <Activity className="w-6 h-6 text-white drop-shadow-md" strokeWidth={2.5} />
            </div>
            <div>
                <h1 className="text-xl font-black tracking-tight text-white leading-tight brand-font bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">Mission-OP</h1>
                <h2 className="text-[10px] font-bold text-indigo-400 tracking-[0.2em] uppercase mt-1">Surgical Suite v3.5</h2>
            </div>
        </div>

        <nav className="space-y-2 flex-1 overflow-y-auto no-scrollbar py-2">
          <NavItem view="dashboard" icon={LayoutDashboard} label="Dashboard" />
          <NavItem view="inpatients" icon={BedDouble} label="Inpatients" />
          <NavItem view="reports" icon={FileText} label="Op Reports" />
          <NavItem view="caselogs" icon={Table} label="Case Logs" />
          <NavItem view="calendar" icon={CalendarDays} label="Schedule" />
          <NavItem view="labs" icon={FlaskConical} label="Lab Manager" />
          <NavItem view="education" icon={BookOpen} label="Education" />
          <NavItem view="drugs" icon={Calculator} label="Drug Index" />
          
          <div className="my-2 border-t border-slate-800 mx-4" />
          <NavItem view="settings" icon={Settings} label="Account Settings" />
        </nav>

        <div className="mt-4 pt-6 border-t border-slate-800">
            <div className="flex items-center gap-4 mb-4 p-4 bg-white/5 rounded-2xl border border-white/5 backdrop-blur-md shadow-inner group transition-colors hover:bg-white/10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 text-white flex items-center justify-center font-bold font-serif shadow-lg border border-white/10 text-lg group-hover:scale-105 transition-transform">
                  {user.firstName ? user.firstName.charAt(0) : ''}{user.lastName.charAt(0)}
              </div>
              <div className="overflow-hidden flex-1">
                <div className="text-sm font-bold text-white font-serif truncate">
                    {user.role === 'Resident' || user.role === 'Attending' ? `Dr. ${user.lastName}` : `${user.firstName} ${user.lastName}`}
                </div>
                <div className="text-[10px] text-indigo-300 font-bold uppercase tracking-wide font-sans truncate">{user.role} {(user.isAdmin || user.id === '8' || user.email === 'kevinsgrisham@gmail.com') && '• Admin'}</div>
              </div>
              <button onClick={() => setCurrentView('settings')} className="text-slate-400 hover:text-white transition-colors">
                  <Settings className="w-4 h-4" />
              </button>
            </div>
            
            <button 
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 text-xs font-bold text-slate-400 hover:text-red-400 hover:bg-red-500/10 p-3 rounded-xl transition-all duration-200 font-sans group border border-transparent hover:border-red-500/20"
            >
              <LogOut className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Sign Out
            </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-slate-900 text-white border-b border-white/10 flex items-center justify-between px-4 z-20 shadow-lg">
         <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-indigo-600 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-lg brand-font">Mission-OP</span>
         </div>
         <button onClick={handleLogout} className="text-slate-400 hover:text-white p-2">
           <LogOut className="w-5 h-5" />
         </button>
      </div>

      <main className="flex-1 overflow-hidden relative pt-16 md:pt-0 bg-slate-950">
        <div className="relative z-10 h-full">
            {renderContent()}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-white/10 flex justify-around p-3 z-30 safe-area-pb">
        {isViewAllowed('dashboard') && (
            <button onClick={() => setCurrentView('dashboard')} className={`p-3 rounded-2xl transition-all ${currentView === 'dashboard' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/50' : 'text-slate-400'}`}>
                <LayoutDashboard className="w-6 h-6" />
            </button>
        )}
        {isViewAllowed('inpatients') && (
            <button onClick={() => setCurrentView('inpatients')} className={`p-3 rounded-2xl transition-all ${currentView === 'inpatients' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/50' : 'text-slate-400'}`}>
                <BedDouble className="w-6 h-6" />
            </button>
        )}
        {isViewAllowed('reports') && (
            <button onClick={() => setCurrentView('reports')} className={`p-3 rounded-2xl transition-all ${currentView === 'reports' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/50' : 'text-slate-400'}`}>
                <FileText className="w-6 h-6" />
            </button>
        )}
         <button onClick={() => setCurrentView('settings')} className={`p-3 rounded-2xl transition-all ${currentView === 'settings' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/50' : 'text-slate-400'}`}>
            <Settings className="w-6 h-6" />
        </button>
      </nav>
    </div>
  );
};

export default App;
